import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { projects } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];
const featured = projects.slice(0, 5);
const AUTO_INTERVAL = 4200;

export default function FeaturedProjects() {
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused) return;
        const id = setInterval(() => {
            setActive((a) => (a + 1) % featured.length);
        }, AUTO_INTERVAL);
        return () => clearInterval(id);
    }, [paused]);

    const p = featured[active];

    return (
        <section
            id="projects"
            data-testid="featured-projects-section"
            className="relative bg-forest-950 text-ivory py-24 md:py-40 overflow-hidden"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {/* Header */}
            <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                <div className="grid grid-cols-1 md:grid-cols-12 items-end mb-14 md:mb-20 gap-8">
                    <div className="md:col-span-8">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="h-px w-10 bg-lime" />
                            <span className="font-mono-mini text-[10px] tracking-[0.42em] uppercase text-lime">
                                Selected collection · Ongoing, completed, upcoming
                            </span>
                        </div>
                        <h2 className="font-serif-display text-4xl md:text-6xl lg:text-[5.5rem] leading-[0.94] tracking-tight max-w-[14ch]">
                            Places worth <em className="text-lime not-italic italic">arriving at.</em>
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/projects"
                            data-testid="view-all-projects"
                            className="inline-flex items-center gap-3 font-mono-mini text-[11px] tracking-[0.32em] uppercase text-ivory hover-underline hover:text-lime transition-colors"
                        >
                            View all projects
                            <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </div>

            {/* Interactive showcase */}
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
                {/* LEFT — Big preview */}
                <div className="md:col-span-7 relative">
                    <Link
                        to={`/projects/${p.slug}`}
                        data-testid={`featured-preview-${p.slug}`}
                        className="group block relative aspect-[16/11] overflow-hidden bg-forest-800"
                    >
                        <AnimatePresence mode="sync">
                            <motion.img
                                key={p.slug}
                                src={p.cover}
                                alt={p.name}
                                initial={{ opacity: 0, scale: 1.08 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 1.02 }}
                                transition={{ duration: 1.15, ease: EASE }}
                                className="absolute inset-0 h-full w-full object-cover will-change-transform"
                            />
                        </AnimatePresence>
                        <div className="absolute inset-0 bg-gradient-to-t from-forest-950/80 via-transparent to-transparent" />

                        {/* Status badge with pulse */}
                        <div className="absolute top-6 left-6 flex items-center gap-2.5">
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-lime opacity-75 animate-ping" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-lime" />
                            </span>
                            <span className="font-mono-mini text-[10px] tracking-[0.3em] uppercase text-ivory">
                                {p.status}
                            </span>
                        </div>

                        {/* Counter top-right */}
                        <div className="absolute top-6 right-6 font-mono-mini text-[10px] tracking-[0.32em] uppercase text-ivory/70">
                            {String(active + 1).padStart(2, "0")} / {String(featured.length).padStart(2, "0")}
                        </div>

                        {/* Auto-progress bar */}
                        <div className="absolute bottom-0 inset-x-0 h-[2px] bg-white/10">
                            <motion.div
                                key={`bar-${active}-${paused}`}
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: paused ? 0.2 : 1 }}
                                transition={{ duration: paused ? 0 : AUTO_INTERVAL / 1000, ease: "linear" }}
                                style={{ transformOrigin: "left" }}
                                className="h-full bg-lime"
                            />
                        </div>

                        {/* Caption bottom */}
                        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
                            <div>
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={p.slug + "-title"}
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        exit={{ y: -12, opacity: 0 }}
                                        transition={{ duration: 0.55, ease: EASE }}
                                    >
                                        <h3 className="font-serif-display text-3xl md:text-4xl lg:text-[3rem] leading-none tracking-tight text-ivory">
                                            {p.name}
                                        </h3>
                                        <div className="flex items-center gap-2 mt-3 text-ivory/70 text-sm">
                                            <MapPin size={13} /> {p.location}
                                        </div>
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                            <div className="h-14 w-14 md:h-16 md:w-16 bg-lime text-forest-900 flex items-center justify-center group-hover:rotate-45 transition-transform duration-500 shrink-0">
                                <ArrowUpRight size={22} strokeWidth={1.5} />
                            </div>
                        </div>
                    </Link>
                </div>

                {/* RIGHT — List of projects */}
                <div className="md:col-span-5">
                    <ul className="border-t border-white/10">
                        {featured.map((f, i) => {
                            const isActive = i === active;
                            return (
                                <li
                                    key={f.slug}
                                    data-testid={`featured-list-item-${f.slug}`}
                                    onMouseEnter={() => setActive(i)}
                                    className="border-b border-white/10"
                                >
                                    <Link to={`/projects/${f.slug}`} className="group block relative py-5 md:py-6">
                                        {/* Sliding lime background */}
                                        <motion.span
                                            animate={{ width: isActive ? "100%" : "0%" }}
                                            transition={{ duration: 0.55, ease: EASE }}
                                            className="absolute left-0 top-0 bottom-0 bg-lime/5 pointer-events-none"
                                        />
                                        <div className="relative flex items-center justify-between gap-4">
                                            <div className="flex items-baseline gap-4 md:gap-6 min-w-0">
                                                <span className={`font-mono-mini text-[10px] tracking-[0.32em] uppercase transition-colors ${isActive ? "text-lime" : "text-ivory/40"}`}>
                                                    0{i + 1}
                                                </span>
                                                <div className="min-w-0">
                                                    <h4 className={`font-serif-display text-2xl md:text-[2rem] leading-tight tracking-tight transition-colors truncate ${isActive ? "text-lime" : "text-ivory"}`}>
                                                        {f.name}
                                                    </h4>
                                                    <div className="mt-1 font-mono-mini text-[10px] tracking-[0.28em] uppercase text-ivory/50">
                                                        {f.type} · {f.year}
                                                    </div>
                                                </div>
                                            </div>
                                            <motion.div
                                                animate={{
                                                    x: isActive ? 0 : -8,
                                                    opacity: isActive ? 1 : 0,
                                                }}
                                                transition={{ duration: 0.35, ease: EASE }}
                                                className="shrink-0"
                                            >
                                                <ArrowUpRight size={20} className={isActive ? "text-lime" : "text-ivory/50"} />
                                            </motion.div>
                                        </div>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Ambient stats */}
                    <div className="mt-10 grid grid-cols-3 gap-6">
                        {[
                            { k: "37", l: "Delivered" },
                            { k: "12", l: "Ongoing" },
                            { k: "18", l: "Upcoming" },
                        ].map((s, i) => (
                            <motion.div
                                key={s.l}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, ease: EASE, delay: i * 0.06 }}
                                className="pt-6 border-t border-white/10"
                            >
                                <div className="font-serif-display text-3xl md:text-4xl text-ivory leading-none italic">
                                    {s.k}
                                </div>
                                <div className="font-mono-mini text-[10px] tracking-[0.28em] uppercase text-ivory/50 mt-2">
                                    {s.l}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
